import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Select, Spin } from "antd";
import { isArray } from "lodash";

const MultiSelect = ({ value, onChange, options: staticOptions, optionApiCall, optionRender, placeholder, disabled = false, allowClear = true, mode = "tags", style, tokenSeparators, showSearch = true, displayGroups = false }) => {
  const [options, setOptions] = useState(staticOptions || []);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const timeoutRef = useRef(0);

  const updateOptions = input => {
    setOptionsLoading(true);
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      optionApiCall(input || "")
        .then(json => {
          setOptions(json);
          setOptionsLoading(false);
        })
        .catch(() => {
          setOptions([]);
          setOptionsLoading(false);
        });
    }, 500);
  };

  const selectOnChange = val => {
    if (onChange) onChange(val);
  };

  const normalizedValue = isArray(value) ? value.map(String) : (value !== undefined && value !== null ? String(value) : value);
  const selectValue = mode !== "default" && normalizedValue && !isArray(normalizedValue) ? [normalizedValue] : normalizedValue;

  if (displayGroups) {
    const groupedOptions = {};
    options.forEach(option => {
      const [key, val] = option.split(" - ");
      if (groupedOptions[key]) groupedOptions[key].push(val);
      else groupedOptions[key] = [val];
    });
    return (
      <Select
        value={selectValue}
        placeholder={placeholder}
        onChange={selectOnChange}
        mode={mode}
        optionLabelProp="label"
        allowClear={allowClear}
        disabled={disabled}
        onSearch={optionApiCall ? updateOptions : undefined}
        onFocus={optionApiCall ? e => updateOptions(e.target.value) : undefined}
        notFoundContent={optionsLoading ? <Spin /> : "Not Found"}
        filterOption={optionApiCall === undefined}
        showSearch={showSearch}
        style={style}
        tokenSeparators={tokenSeparators}
        popupRender={menu => (
          <>
            {menu}
            {optionsLoading && options.length > 0 && (
              <div style={{ textAlign: "center", padding: "8px 0" }}>
                <Spin />
              </div>
            )}
          </>
        )}
      >
        {Object.keys(groupedOptions).map(groupKey => (
          <Select.OptGroup label={groupKey} key={groupKey}>
            <Select.Option value={`select-all-${groupKey}`} key={`select-all-${groupKey}`}>--- SELECT ALL ---</Select.Option>
            {groupedOptions[groupKey].map(opt => (
              <Select.Option value={opt} key={opt}>
                {opt}
              </Select.Option>
            ))}
          </Select.OptGroup>
        ))}
      </Select>
    );
  }

  return (
    <Select
      value={selectValue}
      placeholder={placeholder}
      onChange={selectOnChange}
      mode={mode}
      optionLabelProp="label"
      allowClear={allowClear}
      disabled={disabled}
      onSearch={optionApiCall ? updateOptions : undefined}
      onFocus={optionApiCall ? e => updateOptions(e.target.value) : undefined}
      notFoundContent={optionsLoading ? <Spin /> : "Not Found"}
      filterOption={false}
      showSearch={showSearch}
      style={style}
      tokenSeparators={tokenSeparators}
      popupRender={menu => (
        <>
          {menu}
          {optionsLoading && options.length > 0 && (
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              <Spin />
            </div>
          )}
        </>
      )}
    >
      {options.map((v, i) => {
        const label = optionRender ? optionRender(v) : String(v);
        return (
          <Select.Option value={v} key={i} label={label}>
            {label}
          </Select.Option>
        );
      })}
    </Select>
  );
};

MultiSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]))]),
  onChange: PropTypes.func,
  options: PropTypes.array,
  optionApiCall: PropTypes.func,
  optionRender: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  allowClear: PropTypes.bool,
  mode: PropTypes.oneOf(["tags", "multiple", "default"]),
  tokenSeparators: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  showSearch: PropTypes.bool,
  displayGroups: PropTypes.bool,
  style: PropTypes.object
};

export default MultiSelect;
